// Analytics Page — Sales Analytics Dashboard

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from '../components/layout/MainLayout.jsx';
import { fetchAnalytics, setPeriod } from '../redux/slices/analyticsSlice';
import { analyticsApi } from '../api/analyticsApi';
import {
  FiActivity,
  FiDollarSign,
  FiCheckCircle,
  FiUsers,
  FiHome,
  FiGlobe,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiUser,
  FiPhone,
  FiClock,
  FiPackage,
  FiRefreshCw,
  FiFilter,
} from 'react-icons/fi';

// ─── Constants ────────────────────────────────────────────────────────────────

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week',  label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year',  label: 'Year' },
];

// Real inquiry statuses (must match CustomerInquiry.STATUS_CHOICES) with
// human labels — never show raw keys like `discount_requested` to admin.
const STATUS_META = {
  new:                { label: 'New Inquiry',        color: 'bg-blue-100 text-blue-700 border-blue-200' },
  pending:            { label: 'Pending',            color: 'bg-violet-100 text-violet-700 border-violet-200' },
  replied:            { label: 'Replied',            color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  discount_requested: { label: 'Discount Requested', color: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  waiting_stock:      { label: 'Waiting for Stock',  color: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmed:          { label: 'Confirmed',          color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed:          { label: 'Completed',          color: 'bg-teal-100 text-teal-700 border-teal-200' },
  closed:             { label: 'Closed',             color: 'bg-rose-100 text-rose-600 border-rose-200' },
};

// Funnel stages — same grouping the whole system uses (STAGE_MAP).
const FUNNEL_STAGES = [
  { key: 'pending', label: 'Pending',           hint: 'Waiting for a price',      bar: 'bg-violet-400', dot: 'text-violet-600 bg-violet-100' },
  { key: 'live',    label: 'Live',              hint: 'Customer being worked',    bar: 'bg-cyan-400',   dot: 'text-cyan-600 bg-cyan-100' },
  { key: 'waiting', label: 'Waiting for Stock', hint: 'Part not available yet',   bar: 'bg-amber-400',  dot: 'text-amber-600 bg-amber-100' },
  { key: 'won',     label: 'Sold',              hint: 'Payment confirmed',        bar: 'bg-emerald-400',dot: 'text-emerald-600 bg-emerald-100' },
  { key: 'loss',    label: 'Lost',              hint: 'No deal',                  bar: 'bg-rose-400',   dot: 'text-rose-600 bg-rose-100' },
];

// Post-sale outcomes — NOT funnel stages. Cancel = cash refunded before the
// part was dispatched (line voided); Return = cash refunded after delivery.
const RETURN_BARS = [
  { key: 'cancel', label: 'Cancel', hint: 'Refunded before dispatch', bar: 'bg-orange-400' },
  { key: 'return', label: 'Return', hint: 'Refunded after delivery',  bar: 'bg-rose-500' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n != null
    ? new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n)
    : '—';

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard = ({ title, value, sub, icon: Icon, accent }) => (
  <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 flex gap-4 items-start">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{title}</p>
      <p className="text-2xl font-black text-slate-900 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Funnel Stage Bar ─────────────────────────────────────────────────────────

// A clickable funnel stage — tap to see the inquiries inside it. `value` (₹)
// is shown alongside the count for the post-sale Cancel/Return bars.
const FunnelStage = ({ label, hint, count, pct, barColor, onOpen, value }) => (
  <button
    type="button"
    onClick={onOpen}
    disabled={!count || !onOpen}
    className={`w-full flex flex-col gap-1.5 rounded-xl px-3 py-2 -mx-3 text-left transition-colors ${count && onOpen ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'}`}
    title={count && onOpen ? `View ${label} inquiries` : label}
  >
    <div className="flex items-center justify-between text-sm">
      <span className="font-semibold text-slate-700">
        {label}
        {hint && <span className="ml-2 text-[11px] font-normal text-slate-400">{hint}</span>}
      </span>
      <span className="font-bold text-slate-900">
        {value != null && <span className="text-slate-400 font-medium text-xs mr-1.5">{fmt(value)}</span>}
        {count}
        <span className="text-slate-400 font-medium text-xs ml-1">({pct}%)</span>
      </span>
    </div>
    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ${barColor}`}
        style={{ width: `${Math.min(Number(pct), 100)}%` }}
      />
    </div>
  </button>
);

// ─── Section Card ─────────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
    {title && (
      <div className="px-6 py-4 border-b border-slate-100">
        <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner = () => (
  <div className="flex items-center justify-center py-24">
    <svg className="animate-spin w-8 h-8 text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

// ─── Avatar Cell ──────────────────────────────────────────────────────────────

const Avatar = ({ name, gradient }) => (
  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0 ${gradient}`}>
    {name.slice(0, 2).toUpperCase()}
  </div>
);

// ─── Sales by Source (CAP vs OSM) ─────────────────────────────────────────────

const fmt0 = (n) =>
  n != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
    : '—';

// A single clickable source row (CAP or OSM) inside a period column.
const SourceRow = ({ src, count, value, onOpen }) => {
  const isCap = src === 'cap';
  const Icon = isCap ? FiHome : FiGlobe;
  const tone = isCap
    ? 'bg-violet-50 border-violet-100 hover:bg-violet-100/70'
    : 'bg-amber-50 border-amber-100 hover:bg-amber-100/70';
  const iconTone = isCap ? 'bg-violet-100 text-violet-600' : 'bg-amber-100 text-amber-600';
  const labelTone = isCap ? 'text-violet-700' : 'text-amber-700';
  const disabled = !count;
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      className={`w-full flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors ${tone} ${disabled ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
      title={disabled ? 'No items' : `View ${isCap ? 'CAP' : 'OSM'} items`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${iconTone}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <p className={`text-xs font-bold leading-none ${labelTone}`}>{isCap ? 'CAP' : 'OSM'}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{count} item{count === 1 ? '' : 's'}{!disabled && ' · view'}</p>
        </div>
      </div>
      <span className="text-sm font-black text-slate-900">{fmt0(value)}</span>
    </button>
  );
};

// One sub-period card (Today / a week / a month / a year) with a CAP vs OSM
// split. `bucket` carries from/to so a click drills into that exact range.
const SourceColumn = ({ bucket, onOpen }) => {
  const cap = bucket?.cap || { count: 0, value: 0 };
  const osm = bucket?.osm || { count: 0, value: 0 };
  const total = bucket?.total || { count: 0, value: 0 };
  const totalVal = Number(total.value) || 0;
  const capShare = totalVal > 0 ? (Number(cap.value) / totalVal) * 100 : 0;
  const osmShare = totalVal > 0 ? (Number(osm.value) / totalVal) * 100 : 0;
  const empty = totalVal === 0;

  return (
    <div className={`rounded-2xl border bg-white p-4 ${bucket?.is_current ? 'border-violet-300 ring-1 ring-violet-200' : 'border-slate-200/80'} ${empty ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
          {bucket?.label}
          {bucket?.is_current && <span className="ml-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-600 align-middle">NOW</span>}
        </span>
        <span className="text-sm font-black text-slate-900">{fmt0(totalVal)}</span>
      </div>

      {/* Split share bar */}
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100 mb-3">
        <div className="h-full bg-violet-500 transition-all duration-700" style={{ width: `${capShare}%` }} title={`CAP ${capShare.toFixed(0)}%`} />
        <div className="h-full bg-amber-400 transition-all duration-700" style={{ width: `${osmShare}%` }} title={`OSM ${osmShare.toFixed(0)}%`} />
      </div>

      {empty ? (
        <p className="py-2 text-center text-[11px] text-slate-300">No sales</p>
      ) : (
        <div className="space-y-2">
          <SourceRow src="cap" count={cap.count} value={cap.value} onOpen={() => onOpen('cap', bucket)} />
          <SourceRow src="osm" count={osm.count} value={osm.value} onOpen={() => onOpen('osm', bucket)} />
        </div>
      )}
    </div>
  );
};

// Drill-down modal — a scalable, paginated & searchable browser of the sold
// items behind a CAP/OSM total for a specific date range (the clicked bucket).
const PAGE_SIZE = 25;

const SourceListModal = ({ source, bucket, onClose }) => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCap = source === 'cap';
  const rangeFrom = bucket?.from;
  const rangeTo = bucket?.to;

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // debounce the search box so typing doesn't hammer the server
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // any filter change resets to page 1
  useEffect(() => { setPage(1); }, [debouncedQ]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    analyticsApi
      .getSalesBySourceList({ source, from: rangeFrom, to: rangeTo, q: debouncedQ, page, pageSize: PAGE_SIZE })
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [source, rangeFrom, rangeTo, debouncedQ, page]);

  const items = data?.items || [];
  const count = data?.count ?? 0;
  const numPages = data?.num_pages ?? 1;
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);

  // Source-branded palette — violet for CAP (own stock), amber for OSM (market)
  const theme = isCap
    ? {
        gradient: 'from-violet-600 via-violet-600 to-indigo-600',
        chip: 'bg-white/15 text-white',
        iconBox: 'bg-white/20 text-white',
        rowIcon: 'bg-violet-50 text-violet-500',
        ring: 'focus:border-violet-400 focus:ring-violet-200',
        pill: 'bg-violet-600',
      }
    : {
        gradient: 'from-amber-500 via-orange-500 to-orange-600',
        chip: 'bg-white/15 text-white',
        iconBox: 'bg-white/20 text-white',
        rowIcon: 'bg-amber-50 text-amber-500',
        ring: 'focus:border-amber-400 focus:ring-amber-200',
        pill: 'bg-amber-500',
      };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-3xl bg-slate-50 shadow-2xl overflow-hidden flex flex-col max-h-[88vh] animate-fade-in-up">

        {/* ── Branded hero header ── */}
        <div className={`relative shrink-0 bg-gradient-to-br ${theme.gradient} px-6 pt-5 pb-6 text-white`}>
          {/* soft decorative circles */}
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute right-16 -bottom-12 h-28 w-28 rounded-full bg-white/5" />

          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${theme.iconBox} backdrop-blur-sm`}>
                {isCap ? <FiHome className="h-5 w-5" /> : <FiGlobe className="h-5 w-5" />}
              </div>
              <div>
                <h2 className="text-lg font-black leading-tight">
                  {isCap ? 'CAP Sales' : 'OSM Sales'}{bucket?.label ? ` · ${bucket.label}` : ''}
                </h2>
                <p className="text-xs font-medium text-white/70">
                  {isCap ? 'Sold from CAP’s own stock' : 'Sourced from the open spare market'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white/90 transition hover:bg-white/25"
              aria-label="Close"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          {/* headline numbers */}
          <div className="relative mt-4 flex items-end gap-6">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/60">Total value</p>
              <p className="text-3xl font-black leading-none tracking-tight">
                {data ? fmt0(Number(data.total_value)) : '—'}
              </p>
            </div>
            <div className="pb-0.5">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${theme.chip}`}>
                <FiPackage className="h-3.5 w-3.5" />
                {data ? `${count} item${count === 1 ? '' : 's'}` : '…'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Controls: search (range fixed by the clicked bucket) ── */}
        <div className="shrink-0 border-b border-slate-200/70 bg-white px-5 py-3 flex flex-col sm:flex-row gap-3 sm:items-center">
          {(rangeFrom || rangeTo) && (
            <span className="inline-flex items-center gap-1.5 self-start rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">
              <FiClock className="h-3.5 w-3.5" /> {rangeFrom === rangeTo ? rangeFrom : `${rangeFrom} → ${rangeTo}`}
            </span>
          )}
          <div className="relative flex-1">
            <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search product, customer, phone, car…"
              className={`w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-9 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition-all focus:outline-none focus:ring-2 ${theme.ring}`}
            />
            {q && (
              <button
                onClick={() => setQ('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300"
                aria-label="Clear search"
              >
                <FiX className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* ── List ── */}
        <div className="flex-1 overflow-y-auto min-h-[220px] px-4 py-3">
          {loading ? (
            <div className="py-16 flex justify-center">
              <svg className="animate-spin w-7 h-7 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : items.length === 0 ? (
            <div className="py-14 flex flex-col items-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
                <FiPackage className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold text-slate-600">
                {debouncedQ ? 'No matching items' : `No ${isCap ? 'CAP' : 'OSM'} sales in this period`}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {debouncedQ ? 'Try a different name, phone or part.' : 'Items appear here as sales are confirmed.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => (
                <li
                  key={it.part_id}
                  className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md"
                >
                  {/* top row: product + price */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${theme.rowIcon}`}>
                        <FiPackage className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-900">
                          {it.product_name}
                          {it.quantity > 1 && (
                            <span className="ml-1.5 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 align-middle">
                              ×{it.quantity}
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 truncate text-xs font-medium text-slate-400">{it.car}</p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-base font-black tracking-tight text-slate-900">{fmt0(Number(it.price))}</p>
                      {it.dispatched ? (
                        <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">
                          <FiCheckCircle className="h-2.5 w-2.5" /> Delivered
                        </span>
                      ) : (
                        <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-600">
                          <FiClock className="h-2.5 w-2.5" /> Sold
                        </span>
                      )}
                    </div>
                  </div>

                  {/* bottom row: customer / salesperson / date */}
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-50 pt-2 text-[11px] font-medium text-slate-500">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <FiUser className="h-3 w-3 shrink-0 text-slate-300" />
                      <span className="truncate">{it.customer_name || 'Customer'}</span>
                    </span>
                    {it.customer_phone && (
                      <span className="inline-flex items-center gap-1.5">
                        <FiPhone className="h-3 w-3 shrink-0 text-slate-300" />
                        {it.customer_phone}
                      </span>
                    )}
                    {it.salesperson_name && (
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <FiUsers className="h-3 w-3 shrink-0 text-slate-300" />
                        <span className="truncate">{it.salesperson_name}</span>
                      </span>
                    )}
                    <span className="ml-auto inline-flex items-center gap-1.5 text-slate-400">
                      <FiClock className="h-3 w-3 shrink-0 text-slate-300" />
                      {it.sold_at}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Pagination footer ── */}
        <div className="shrink-0 border-t border-slate-200/70 bg-white px-5 py-3 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">
            {count === 0 ? 'No results' : (
              <>Showing <span className="font-bold text-slate-700">{from}–{to}</span> of{' '}
              <span className="font-bold text-slate-700">{count.toLocaleString('en-IN')}</span></>
            )}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[3.5rem] text-center text-xs font-bold text-slate-600 tabular-nums">
              {page} / {numPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(numPages, p + 1))}
              disabled={loading || page >= numPages}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PERIOD_HINT = {
  today: 'today',
  week: 'each week of this month',
  month: 'each month of this year',
  year: 'each year with sales',
};

const SourceSplitSection = ({ period = 'today', refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { source, bucket }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    analyticsApi
      .getSalesBySource(period)
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, refreshKey]);

  const openList = (source, bucket) => setDrill({ source, bucket });
  const buckets = data?.buckets || [];

  return (
    <Section title="Sales by Source — CAP vs OSM">
      {loading ? (
        <div className="py-10 flex justify-center">
          <svg className="animate-spin w-6 h-6 text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            Sold parts (payment confirmed), net of cash refunds, split into {PERIOD_HINT[period] || 'periods'} — follows the period filter above. Tap a CAP or OSM row to see the items.
          </p>
          {buckets.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No data for this period.</p>
          ) : (
            <div className={`grid gap-3 ${
              buckets.length <= 1 ? 'grid-cols-1'
                : buckets.length <= 3 ? 'grid-cols-1 sm:grid-cols-3'
                : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
            }`}>
              {buckets.map((b) => (
                <SourceColumn key={b.label} bucket={b} onOpen={openList} />
              ))}
            </div>
          )}
        </>
      )}

      {drill && (
        <SourceListModal source={drill.source} bucket={drill.bucket} onClose={() => setDrill(null)} />
      )}
    </Section>
  );
};

// ─── Funnel drill-down modal ──────────────────────────────────────────────────
// The inquiries behind a funnel stage / status pill — paginated + searchable,
// same premium pattern as the CAP/OSM browser.
const FUNNEL_MODAL_PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const FunnelListModal = ({ initialPeriod, stage, status, title, hint, onClose }) => {
  const [period, setPeriod] = useState(initialPeriod);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [period, debouncedQ]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    analyticsApi
      .getFunnelInquiries({ period, stage, status, q: debouncedQ, page, pageSize: PAGE_SIZE })
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, stage, status, debouncedQ, page]);

  const items = data?.items || [];
  const count = data?.count ?? 0;
  const numPages = data?.num_pages ?? 1;
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-3xl bg-slate-50 shadow-2xl overflow-hidden flex flex-col max-h-[88vh] animate-fade-in-up">

        {/* ── Hero header ── */}
        <div className="relative shrink-0 bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 px-6 pt-5 pb-6 text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-white/5" />
          <div className="pointer-events-none absolute right-16 -bottom-12 h-28 w-28 rounded-full bg-white/5" />

          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-sm">
                <FiFilter className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-black leading-tight">{title}</h2>
                {hint && <p className="text-xs font-medium text-white/60">{hint}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white/90 transition hover:bg-white/25"
              aria-label="Close"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          <div className="relative mt-4 flex items-end gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/50">Inquiries</p>
              <p className="text-3xl font-black leading-none tracking-tight">
                {data ? count.toLocaleString('en-IN') : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* ── Controls ── */}
        <div className="shrink-0 border-b border-slate-200/70 bg-white px-5 py-3 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1 self-start">
            {FUNNEL_MODAL_PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  period === p.value ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1">
            <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, phone, car, part…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-9 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition-all focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
            {q && (
              <button
                onClick={() => setQ('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300"
                aria-label="Clear search"
              >
                <FiX className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* ── List ── */}
        <div className="flex-1 overflow-y-auto min-h-[220px] px-4 py-3">
          {loading ? (
            <div className="py-16 flex justify-center">
              <svg className="animate-spin w-7 h-7 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : items.length === 0 ? (
            <div className="py-14 flex flex-col items-center text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
                <FiActivity className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold text-slate-600">
                {debouncedQ ? 'No matching inquiries' : 'No inquiries here for this period'}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {debouncedQ ? 'Try a different name, phone or part.' : 'Change the period above to widen the range.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => {
                const meta = STATUS_META[it.status] || { label: it.status_label || it.status, color: 'bg-slate-100 text-slate-500 border-slate-200' };
                return (
                  <li
                    key={it.inquiry_id}
                    className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                          <FiUser className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-900">{it.customer_name || 'Customer'}</p>
                          <p className="mt-0.5 truncate text-xs font-medium text-slate-400">{it.car}</p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        {it.quoted_price != null && Number(it.quoted_price) > 0 && (
                          <p className="text-base font-black tracking-tight text-slate-900">{fmt0(Number(it.quoted_price))}</p>
                        )}
                        <span className={`mt-0.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.color}`}>
                          {meta.label}
                        </span>
                      </div>
                    </div>

                    {it.parts_preview && (
                      <p className="mt-2 truncate text-xs text-slate-500">
                        <FiPackage className="mr-1.5 inline h-3 w-3 text-slate-300" />
                        {it.parts_preview}
                        {it.parts_count > 0 && <span className="text-slate-300"> · {it.parts_count} part{it.parts_count === 1 ? '' : 's'}</span>}
                      </p>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-50 pt-2 text-[11px] font-medium text-slate-500">
                      {it.customer_phone && (
                        <span className="inline-flex items-center gap-1.5">
                          <FiPhone className="h-3 w-3 shrink-0 text-slate-300" />
                          {it.customer_phone}
                        </span>
                      )}
                      {it.salesperson_name && (
                        <span className="inline-flex items-center gap-1.5 min-w-0">
                          <FiUsers className="h-3 w-3 shrink-0 text-slate-300" />
                          <span className="truncate">{it.salesperson_name}</span>
                        </span>
                      )}
                      <span className="ml-auto inline-flex items-center gap-1.5 text-slate-400">
                        <FiClock className="h-3 w-3 shrink-0 text-slate-300" />
                        {it.created_at}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ── Pagination footer ── */}
        <div className="shrink-0 border-t border-slate-200/70 bg-white px-5 py-3 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">
            {count === 0 ? 'No results' : (
              <>Showing <span className="font-bold text-slate-700">{from}–{to}</span> of{' '}
              <span className="font-bold text-slate-700">{count.toLocaleString('en-IN')}</span></>
            )}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[3.5rem] text-center text-xs font-bold text-slate-600 tabular-nums">
              {page} / {numPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(numPages, p + 1))}
              disabled={loading || page >= numPages}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-40 disabled:shadow-none"
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const { data, loading, error, period } = useSelector((s) => s.analytics);
  const [funnelDrill, setFunnelDrill] = useState(null); // { stage?, status?, title, hint }
  const [sourceRefreshKey, setSourceRefreshKey] = useState(0);

  useEffect(() => {
    dispatch(fetchAnalytics(period));
  }, [dispatch, period]);

  const refreshAll = () => {
    dispatch(fetchAnalytics(period));
    setSourceRefreshKey((k) => k + 1); // re-pulls the CAP/OSM split too
  };

  const funnel    = data?.inquiry_funnel;
  const revenue   = data?.revenue;
  const customers = data?.customers;
  const byStatus  = funnel?.by_status || {};
  const stages    = funnel?.stages || {};
  const statusLabels = funnel?.status_labels || {};

  return (
    <MainLayout>
      <div className="space-y-6">

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {data?.generated_at ? `Updated ${data.generated_at}` : 'Sales performance overview'}
            </p>
          </div>

          {/* Period picker + refresh */}
          <div className="flex items-center gap-2 self-start">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => dispatch(setPeriod(p.value))}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                    period === p.value
                      ? 'bg-white text-violet-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={refreshAll}
              disabled={loading}
              className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 sm:px-4 text-xs sm:text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              title="Reload all analytics"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* ── Loading ── */}
        {loading && <Spinner />}

        {/* ── Error ── */}
        {!loading && error && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-600 text-sm font-medium">
            Failed to load analytics: {error}
          </div>
        )}

        {/* ── Content ── */}
        {!loading && data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <KpiCard
                title="Total Inquiries"
                value={funnel?.total_inquiries ?? 0}
                sub="in selected period"
                icon={FiActivity}
                accent="bg-violet-100 text-violet-600"
              />
              <KpiCard
                title="Total Quoted Value"
                value={fmt(revenue?.total_quoted_value)}
                sub="total quotes raised"
                icon={FiDollarSign}
                accent="bg-amber-100 text-amber-600"
              />
              <KpiCard
                title="Completed Revenue"
                value={fmt(revenue?.completed_revenue)}
                sub={`Avg ${fmt(revenue?.avg_quote_value)} per quote`}
                icon={FiCheckCircle}
                accent="bg-emerald-100 text-emerald-600"
              />
              <KpiCard
                title="Unique Customers"
                value={customers?.total_unique_in_period ?? 0}
                sub={`${customers?.new_customers_in_period ?? 0} new · ${customers?.repeat_customers ?? 0} repeat`}
                icon={FiUsers}
                accent="bg-sky-100 text-sky-600"
              />
            </div>

            {/* Sales by Source — CAP vs OSM (today / month / year) */}
            <SourceSplitSection period={period} refreshKey={sourceRefreshKey} />

            {/* Inquiry Funnel + Revenue */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

              {/* Inquiry Funnel — spans 2 cols */}
              <div className="lg:col-span-2">
                <Section title="Inquiry Funnel">
                  <p className="text-xs text-slate-400 -mt-2 mb-4">Tap any status or stage to see the inquiries inside it.</p>

                  {/* Status breakdown pills — clickable */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {Object.entries(byStatus).map(([key, count]) => {
                      const meta = STATUS_META[key] || {
                        label: statusLabels[key] || key,
                        color: 'bg-slate-100 text-slate-500 border-slate-200',
                      };
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={!count}
                          onClick={() =>
                            setFunnelDrill({ status: key, title: meta.label, hint: 'Inquiries with this status' })
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-transform ${meta.color} ${
                            count ? 'cursor-pointer hover:scale-105 hover:shadow-sm' : 'opacity-40 cursor-default'
                          }`}
                          title={count ? `View ${meta.label} inquiries` : 'None'}
                        >
                          {meta.label}
                          <span className="font-black">{count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Funnel stage bars — clickable */}
                  <div className="space-y-2">
                    {FUNNEL_STAGES.map((st) => (
                      <FunnelStage
                        key={st.key}
                        label={st.label}
                        hint={st.hint}
                        count={stages[st.key]?.count ?? 0}
                        pct={stages[st.key]?.percentage ?? 0}
                        barColor={st.bar}
                        onOpen={() =>
                          setFunnelDrill({ stage: st.key, title: `${st.label} inquiries`, hint: st.hint })
                        }
                      />
                    ))}
                  </div>

                  {/* Post-sale outcomes — Cancel (refund before dispatch) and
                      Return (refund after delivery). Not funnel stages. */}
                  <div className="mt-4 border-t border-dashed border-slate-200 pt-3">
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">After the sale</p>
                    <div className="space-y-2">
                      {RETURN_BARS.map((rb) => (
                        <FunnelStage
                          key={rb.key}
                          label={rb.label}
                          hint={rb.hint}
                          count={funnel?.returns?.[rb.key]?.count ?? 0}
                          pct={funnel?.returns?.[rb.key]?.percentage ?? 0}
                          value={Number(funnel?.returns?.[rb.key]?.value ?? 0)}
                          barColor={rb.bar}
                        />
                      ))}
                    </div>
                  </div>
                </Section>
              </div>

              {/* Revenue Breakdown */}
              <Section title="Revenue Breakdown">
                <div className="space-y-3 h-full">
                  <div className="flex flex-col gap-1 p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Total Quoted</span>
                    <span className="text-2xl font-black text-slate-900">{fmt(revenue?.total_quoted_value)}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Completed</span>
                    <span className="text-2xl font-black text-slate-900">{fmt(revenue?.completed_revenue)}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Avg Quote</span>
                    <span className="text-2xl font-black text-slate-900">{fmt(revenue?.avg_quote_value)}</span>
                  </div>
                </div>
              </Section>
            </div>

            {/* Performance Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Supervisor Performance */}
              <Section title="Supervisor Performance">
                {(data.supervisor_performance?.length ?? 0) === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">No data for this period</p>
                ) : (
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="w-full text-sm min-w-[360px]">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Name</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Quoted</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Completed</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Avg Response</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {data.supervisor_performance.map((sp) => (
                          <tr key={sp.id}>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <Avatar name={sp.name} gradient="bg-gradient-to-br from-violet-500 to-indigo-600" />
                                <span className="font-semibold text-slate-800">{sp.name}</span>
                              </div>
                            </td>
                            <td className="py-3 text-right font-semibold text-slate-700">{sp.total_quoted}</td>
                            <td className="py-3 text-right">
                              <span className={`font-semibold ${sp.completed > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {sp.completed}
                              </span>
                            </td>
                            <td className="py-3 text-right text-slate-400 text-xs">
                              {sp.avg_response_time_hours != null ? `${sp.avg_response_time_hours}h` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* Salesperson Performance */}
              <Section title="Salesperson Performance">
                {(data.salesperson_performance?.length ?? 0) === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">No data for this period</p>
                ) : (
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="w-full text-sm min-w-[360px]">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Name</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Inquiries</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Submit %</th>
                          <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Convert %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {data.salesperson_performance.map((sp) => (
                          <tr key={sp.id}>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-2">
                                <Avatar name={sp.name} gradient="bg-gradient-to-br from-sky-500 to-cyan-600" />
                                <span className="font-semibold text-slate-800">{sp.name}</span>
                              </div>
                            </td>
                            <td className="py-3 text-right font-semibold text-slate-700">{sp.total_inquiries}</td>
                            <td className="py-3 text-right">
                              <span className={`font-semibold ${sp.submission_rate_pct > 0 ? 'text-violet-600' : 'text-slate-400'}`}>
                                {sp.submission_rate_pct}%
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              <span className={`font-semibold ${sp.conversion_rate_pct > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {sp.conversion_rate_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            </div>
          </>
        )}
      </div>

      {funnelDrill && (
        <FunnelListModal
          initialPeriod={period}
          stage={funnelDrill.stage}
          status={funnelDrill.status}
          title={funnelDrill.title}
          hint={funnelDrill.hint}
          onClose={() => setFunnelDrill(null)}
        />
      )}
    </MainLayout>
  );
};

export default AnalyticsPage;
